import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Clock, ChevronDown, Lock, Unlock, Info, RotateCcw, X, Check } from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';
export interface TimeframeOverride {
  timeframeId: string;
  label: string;
}
const TIMEFRAMES = [{
  id: '6h',
  label: 'Next 6 Hours'
}, {
  id: '12h',
  label: 'Next 12 Hours'
}, {
  id: '24h',
  label: 'Next 24 Hours'
}, {
  id: '48h',
  label: 'Next 48 Hours'
}, {
  id: '7d',
  label: 'Next 7 Days'
}, {
  id: '14d',
  label: 'Next 14 Days'
}];
interface TimeframeSelectorProps {
  onOverrideChange?: (override: TimeframeOverride | null) => void;
  mode?: 'override' | 'page';
  /** Timeframe id to pre-select (e.g. '24h') */
  defaultTimeframeId?: string;
}
export function TimeframeSelector({
  onOverrideChange,
  mode = 'override',
  defaultTimeframeId
}: TimeframeSelectorProps) {
  const isPageMode = mode === 'page';
  const {
    theme
  } = useTheme();
  const isLight = theme === 'light';
  const [open, setOpen] = useState(false);
  const [overrideActive, setOverrideActive] = useState(false);
  const [selectedTimeframe, setSelectedTimeframe] = useState<string | null>(defaultTimeframeId && TIMEFRAMES.some(t => t.id === defaultTimeframeId) ? defaultTimeframeId : null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      const target = e.target as Node;
      if (panelRef.current && !panelRef.current.contains(target) && containerRef.current && !containerRef.current.contains(target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);
  const toggleOverride = () => {
    const next = !overrideActive;
    setOverrideActive(next);
    if (next && selectedTimeframe) {
      const tf = TIMEFRAMES.find(t => t.id === selectedTimeframe);
      if (tf) onOverrideChange?.({
        timeframeId: tf.id,
        label: tf.label
      });
    } else {
      onOverrideChange?.(null);
    }
  };
  useEffect(() => {
    if (isPageMode) {
      if (selectedTimeframe) {
        const tf = TIMEFRAMES.find(t => t.id === selectedTimeframe);
        if (tf) onOverrideChange?.({
          timeframeId: tf.id,
          label: tf.label
        });
      } else {
        onOverrideChange?.(null);
      }
    } else {
      if (overrideActive && selectedTimeframe) {
        const tf = TIMEFRAMES.find(t => t.id === selectedTimeframe);
        if (tf) onOverrideChange?.({
          timeframeId: tf.id,
          label: tf.label
        });
      } else if (overrideActive && !selectedTimeframe) {
        setOverrideActive(false);
        onOverrideChange?.(null);
      }
    }
  }, [selectedTimeframe, overrideActive, onOverrideChange, isPageMode]);
  const resetSelection = () => {
    setSelectedTimeframe(null);
    if (overrideActive) {
      setOverrideActive(false);
      onOverrideChange?.(null);
    }
  };
  const getPanelPosition = () => {
    if (!triggerRef.current) return {
      top: 0,
      left: 0
    };
    const rect = triggerRef.current.getBoundingClientRect();
    return {
      top: rect.bottom + 8,
      left: rect.left
    };
  };
  const dropdownStyle: React.CSSProperties = {
    background: isLight ? 'rgba(255,255,255,0.97)' : 'rgba(11, 17, 32, 0.97)',
    backdropFilter: 'blur(40px) saturate(180%)',
    WebkitBackdropFilter: 'blur(40px) saturate(180%)'
  };
  const selectedLabel = selectedTimeframe ? TIMEFRAMES.find(t => t.id === selectedTimeframe)?.label : null;
  // 4 most frequent timeframes shown as tabs
  const QUICK_TABS = ['24h', '48h', '7d', '14d'];
  const quickSelect = (id: string) => {
    setSelectedTimeframe(id);
    setOpen(false);
  };
  return <>
      <div ref={containerRef} className="jua-toolbar-btn flex items-center h-8 rounded-lg overflow-hidden flex-shrink-0" style={{
      border: `1px solid ${isLight ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.08)'}`
    }} data-id="element-2204">
        {/* Clock icon label */}
        <div className="flex items-center gap-1 px-2" style={{
        borderRight: `1px solid ${isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.06)'}`
      }} data-id="element-2205">
          {!isPageMode && overrideActive ? <Lock size={11} className="text-amber-400" data-id="element-2206" /> : <Clock size={11} style={{
          color: isLight ? '#94a3b8' : '#64748b'
        }} data-id="element-2207" />}
          <span className="text-[10px] uppercase font-bold" style={{
          color: isLight ? '#64748b' : '#64748b'
        }} data-id="element-2208">
            {!isPageMode && overrideActive ? 'Override' : 'Horizon'}
          </span>
        </div>

        {/* Quick tabs */}
        {QUICK_TABS.map(id => {
        const isActive = selectedTimeframe === id;
        return <button key={id} onClick={() => quickSelect(id)} className="px-2.5 h-full text-[11px] font-medium transition-all last:border-r-0" style={{
          borderRight: `1px solid ${isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.06)'}`,
          backgroundColor: isActive ? 'rgba(236,72,153,0.15)' : 'transparent',
          color: isActive ? '#EC4899' : isLight ? '#475569' : '#94a3b8'
        }} onMouseEnter={e => {
          if (!isActive) {
            e.currentTarget.style.backgroundColor = isLight ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.06)';
            e.currentTarget.style.color = isLight ? '#1e293b' : '#fff';
          }
        }} onMouseLeave={e => {
          if (!isActive) {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = isLight ? '#475569' : '#94a3b8';
          }
        }} data-id="element-2209">
              {id}
            </button>;
      })}

        {/* Dropdown trigger for all options */}
        <button ref={triggerRef} onClick={() => setOpen(!open)} className="flex items-center justify-center w-7 h-full transition-all" style={{
        backgroundColor: open || selectedTimeframe && !QUICK_TABS.includes(selectedTimeframe) ? 'rgba(236,72,153,0.15)' : 'transparent',
        color: open || selectedTimeframe && !QUICK_TABS.includes(selectedTimeframe) ? '#EC4899' : isLight ? '#475569' : '#94a3b8'
      }} title="All timeframes" data-id="element-2210">
          <ChevronDown size={12} className={`transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-2211" />
        </button>
      </div>

      {open && ReactDOM.createPortal(<div ref={panelRef} className="fixed rounded-xl shadow-2xl overflow-hidden z-[9999] flex flex-col" style={{
      ...dropdownStyle,
      top: getPanelPosition().top,
      left: getPanelPosition().left,
      width: 280,
      boxShadow: isLight ? '0 20px 60px rgba(0,0,0,0.15)' : '0 20px 60px rgba(0,0,0,0.6)',
      border: `1px solid ${isLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.08)'}`
    }} data-id="element-2212">
            <div className="absolute inset-0 rounded-xl pointer-events-none" style={{
        background: isLight ? 'linear-gradient(135deg, rgba(0,0,0,0.01) 0%, transparent 50%)' : 'linear-gradient(135deg, rgba(255,255,255,0.04) 0%, transparent 50%, rgba(255,255,255,0.01) 100%)'
      }} data-id="element-2213" />

            <div className="relative flex items-center justify-between px-4 py-3" style={{
        borderBottom: `1px solid ${isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.06)'}`
      }} data-id="element-2214">
              <div className="flex items-center gap-2" data-id="element-2215">
                <h3 className="text-sm font-semibold" style={{
            color: isLight ? '#1e293b' : '#fff'
          }} data-id="element-2216">
                  {isPageMode ? 'Select Timeframe' : 'Timeframe'}
                </h3>
                <Info size={13} className="text-slate-500" data-id="element-2217" />
              </div>
              <div className="flex items-center gap-2" data-id="element-2218">
                <button onClick={resetSelection} className="flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-medium transition-colors" style={{
            color: isLight ? '#64748b' : '#94a3b8'
          }} data-id="element-2219">
                  <RotateCcw size={11} data-id="element-2220" /> Reset
                </button>
                <button onClick={() => setOpen(false)} className="p-1 rounded-md transition-colors" style={{
            color: isLight ? '#94a3b8' : '#64748b'
          }} data-id="element-2221">
                  <X size={14} data-id="element-2222" />
                </button>
              </div>
            </div>

            <div className="relative flex flex-col flex-1 overflow-hidden" style={{
        backgroundColor: isLight ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.02)'
      }} data-id="element-2223">
              <div className="px-2 py-2 space-y-0.5" data-id="element-2224">
                {TIMEFRAMES.map(t => <button key={t.id} onClick={() => setSelectedTimeframe(t.id)} className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left transition-all" style={{
            backgroundColor: selectedTimeframe === t.id ? 'rgba(236,72,153,0.1)' : 'transparent',
            color: selectedTimeframe === t.id ? isLight ? '#1e293b' : '#fff' : isLight ? '#475569' : '#cbd5e1'
          }} data-id="element-2225">
                    <span className="text-xs font-medium" data-id="element-2226">{t.label}</span>
                    {selectedTimeframe === t.id && <Check size={12} className="text-[#EC4899]" data-id="element-2227" />}
                  </button>)}
              </div>
            </div>

            <div className="relative px-4 py-2.5 flex items-center justify-between text-[11px]" style={{
        borderTop: `1px solid ${isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.06)'}`
      }} data-id="element-2228">
              <div className="flex items-center gap-1.5" data-id="element-2229">
                <span style={{
            color: isLight ? '#94a3b8' : '#94a3b8'
          }} data-id="element-2230">
                  Selected:
                </span>
                <span className="font-bold truncate max-w-[100px]" style={{
            color: selectedLabel ? isLight ? '#1e293b' : '#fff' : '#64748b'
          }} data-id="element-2231">
                  {selectedLabel || 'None'}
                </span>
              </div>

              {!isPageMode && <button onClick={toggleOverride} disabled={!selectedTimeframe} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium transition-all border ${overrideActive ? 'bg-amber-500/15 border-amber-500/30 text-amber-400 hover:bg-amber-500/25' : !selectedTimeframe ? 'cursor-not-allowed' : ''}`} style={!overrideActive ? {
          backgroundColor: isLight ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.04)',
          borderColor: isLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.08)',
          color: !selectedTimeframe ? '#64748b' : isLight ? '#475569' : '#94a3b8'
        } : undefined} data-id="element-2232">
                  {overrideActive ? <Lock size={10} data-id="element-2233" /> : <Unlock size={10} data-id="element-2234" />}
                  {overrideActive ? 'Override Active' : 'Override All'}
                </button>}
            </div>
          </div>, document.body)}
    </>;
}